import {
    FC,
    ReactNode,
    useEffect,
    useId,
    useRef,
    useState,
} from 'react';
import type { PointerEvent } from 'react';
import classes from './Modal.module.scss';

export interface ModalProps {
    visible: boolean;
    onHide: () => void;
    title?: string;
    children: ReactNode;
    size?: 'sm' | 'md' | 'lg' | 'xl';
    appearance?: 'default' | 'surface';
    closable?: boolean;
    footer?: ReactNode;
    className?: string;
    draggable?: boolean;
}

const bodyLocks = new Set<symbol>();
let savedBodyOverflow = '';

/** Показывает модальное окно с удержанием фокуса и возвратом к вызвавшему элементу. */
export const Modal: FC<ModalProps> = (
    {
        visible,
        onHide,
        title,
        children,
        size = 'md',
        appearance = 'default',
        closable = true,
        footer,
        className = '',
        draggable = false,
    }
) => {
    const dialogRef = useRef<HTMLDivElement>(null);
    const titleId = useId();

    const [offset, setOffset] = useState({
        x: 0,
        y: 0
    });

    const drag = useRef<{
        pointerId: number;
        x: number;
        y: number;
        offsetX: number;
        offsetY: number;
        bounds: DOMRect;
    } | null>(null);

    useEffect(() => {
        if (visible || !draggable) return;

        drag.current = null;

        setOffset({
            x: 0,
            y: 0
        });
    }, [visible, draggable]);

    /** Начинает перемещение окна за заголовок, исключая кнопки управления */
    const startDrag = (event: PointerEvent<HTMLDivElement>): void => {
        if (!draggable || event.button !== 0 || (event.target as HTMLElement).closest('button')) return;

        const bounds = dialogRef.current?.getBoundingClientRect();

        if (!bounds) return;

        drag.current = {
            pointerId: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            offsetX: offset.x,
            offsetY: offset.y,
            bounds,
        };
        event.currentTarget.setPointerCapture(event.pointerId);
        event.preventDefault();
    };

    /** Перемещает окно внутри видимой области экрана */
    const moveDrag = (event: PointerEvent<HTMLDivElement>): void => {
        const current = drag.current;

        if (!current || event.pointerId !== current.pointerId) return;

        const dx = Math.max(-current.bounds.left, Math.min(event.clientX - current.x, window.innerWidth - current.bounds.right));
        const dy = Math.max(-current.bounds.top, Math.min(event.clientY - current.y, window.innerHeight - current.bounds.bottom));

        setOffset({
            x: current.offsetX + dx,
            y: current.offsetY + dy
        });
    };

    /** Завершает перемещение окна при отпускании или отмене указателя */
    const finishDrag = (event: PointerEvent<HTMLDivElement>): void => {
        if (drag.current?.pointerId !== event.pointerId) return;

        drag.current = null;

        if (event.currentTarget.hasPointerCapture(event.pointerId)) {
            event.currentTarget.releasePointerCapture(event.pointerId);
        }
    };

    useEffect(() => {
        if (!visible) return;

        const previousFocus = document.activeElement instanceof HTMLElement
            ? document.activeElement
            : null;

        const lock = Symbol();

        if (bodyLocks.size === 0) savedBodyOverflow = document.body.style.overflow;
        bodyLocks.add(lock);
        document.body.style.overflow = 'hidden';
        dialogRef.current?.focus();

        return () => {
            bodyLocks.delete(lock);

            if (bodyLocks.size === 0) document.body.style.overflow = savedBodyOverflow;
            previousFocus?.focus();
        };
    }, [visible]);

    useEffect(() => {
        /** Закрывает диалог и удерживает фокус внутри него */
        const handleEscape = (e: KeyboardEvent) => {
            if (e.target instanceof Element && e.target.closest('[role="dialog"]') !== dialogRef.current) return;

            if (e.key === 'Escape' && visible && closable) {
                onHide();
            }

            if (e.key !== 'Tab' || !visible || !dialogRef.current) return;

            const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
            ))
                .filter((element) => element.getClientRects().length > 0);

            if (focusable.length === 0) {
                e.preventDefault();
                dialogRef.current.focus();

                return;
            }

            const first = focusable[0];
            const last = focusable[focusable.length - 1];

            if (!dialogRef.current.contains(document.activeElement)) {
                e.preventDefault();
                first.focus();
            } else {
                const shouldWrapFocus = e.shiftKey && (document.activeElement === first || document.activeElement === dialogRef.current);

                if (shouldWrapFocus) {
                    e.preventDefault();
                    last.focus();
                } else if (!e.shiftKey && document.activeElement === last) {
                    e.preventDefault();
                    first.focus();
                }
            }
        };

        const dialog = dialogRef.current;
        dialog?.addEventListener('keydown', handleEscape);

        return () => dialog?.removeEventListener('keydown', handleEscape);
    }, [visible, closable, onHide]);

    if (!visible) return null;

    const modalClass = `${classes.modal} ${classes[`modal--${size}`]} ${appearance === 'surface'
        ? classes.surface
        : ''} ${className}`;

    return (
        <div
            className={classes.modalOverlay}
            onClick={closable
                ? onHide
                : undefined
            }
        >
            <div
                ref={dialogRef}
                className={modalClass}
                role="dialog"
                aria-modal="true"
                aria-labelledby={title
                    ? titleId
                    : undefined
                }
                aria-label={title
                    ? undefined
                    : 'Диалоговое окно'
                }
                tabIndex={-1}
                style={draggable
                    ? { translate: `${offset.x}px ${offset.y}px` }
                    : undefined
                }
                onClick={(e) => e.stopPropagation()}
                onSubmit={(event) => event.stopPropagation()}
            >
                {title && (
                    <div
                        className={`${classes.modalHeader} ${draggable
                            ? classes.draggableHeader
                            : ''}`}
                        onPointerDown={startDrag}
                        onPointerMove={moveDrag}
                        onPointerUp={finishDrag}
                        onPointerCancel={finishDrag}
                        onLostPointerCapture={() => { drag.current = null; }}
                    >
                        <h3
                            id={titleId}
                            className={classes.modalTitle}
                        >
                            {title}
                        </h3>
                        {closable && (
                            <button
                                type="button"
                                className={classes.closeButton}
                                onClick={onHide}
                                aria-label="Закрыть"
                            >
                                ×
                            </button>
                        )}
                    </div>
                )}

                <div className={classes.modalContent}>
                    {children}
                </div>

                {footer && (
                    <div className={classes.modalFooter}>
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};
